import React, { useState, useEffect } from 'react';
import { Button, SideBarModal } from '@/components';
import { Oem } from '@/types/common';
import { CreateOEMRequest } from '@/services/api/oems';
import dayjs from 'dayjs';

interface AddOEMModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOEMRequest) => void;
  isLoading?: boolean;
  initialData?: Oem | null;
  isEditMode?: boolean;
}

const AddOEMModal: React.FC<AddOEMModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData = null,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<CreateOEMRequest>({
    name: '',
    partnershipStatus: 'ACTIVE',
    dashboardLink: '',
    dashboardUsername: '',
    dashboardPassword: '',
    createdBy: '',
  });

  const [errors, setErrors] = useState<Partial<CreateOEMRequest>>({});
  const [isViewingInfo, setIsViewingInfo] = useState(false);

  useEffect(() => {
    if (isEditMode && initialData) {
      setIsViewingInfo(true);
      setFormData({
        name: (initialData as any).name || '',
        partnershipStatus: (initialData as any).partnershipStatus || 'ACTIVE',
        dashboardLink: initialData.dashboardLink || '',
        dashboardUsername: initialData.dashboardUsername || '',
        dashboardPassword: initialData.dashboardPassword || '',
        createdBy: '',
      });
    } else {
      setIsViewingInfo(false);
      setFormData({
        name: '',
        partnershipStatus: 'ACTIVE',
        dashboardLink: '',
        dashboardUsername: '',
        dashboardPassword: '',
        createdBy: '',
      });
    }
  }, [isEditMode, initialData, isOpen]);

  const handleInputChange = (field: keyof CreateOEMRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePartnershipToggle = () => {
    setFormData((prev) => {
      const newStatus =
        prev.partnershipStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      return {
        ...prev,
        partnershipStatus: newStatus,
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateOEMRequest> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'OEM Name is required';
    }

    if (!formData.dashboardLink.trim()) {
      newErrors.dashboardLink = 'Dashboard Link is required';
    }

    if (!formData.dashboardUsername.trim()) {
      newErrors.dashboardUsername = 'Dashboard Username is required';
    }

    if (!formData.dashboardPassword.trim()) {
      newErrors.dashboardPassword = 'Dashboard Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      partnershipStatus: 'ACTIVE',
      dashboardLink: '',
      dashboardUsername: '',
      dashboardPassword: '',
      createdBy: '',
    });
    setErrors({});
    setIsViewingInfo(false);
    onClose();
  };

  const handleEditClick = () => {
    setIsViewingInfo(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit OEM' : 'Add OEM'}
            </h2>
            <p className="text-sm font-normal text-gray-600 mt-1">
              {isEditMode
                ? 'Update OEM partner details.'
                : 'Create a new OEM partner.'}
            </p>
          </div>
        }
        footer={
          isEditMode && isViewingInfo ? (
            <Button
              variant="primary"
              onClick={handleEditClick}
              className="w-full py-3"
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                const fakeEvent = {
                  preventDefault: () => {},
                } as React.FormEvent;
                handleSubmit(fakeEvent);
              }}
              disabled={
                isLoading ||
                !formData.name.trim() ||
                !formData.dashboardLink.trim() ||
                !formData.dashboardUsername.trim() ||
                !formData.dashboardPassword.trim()
              }
              className="w-full py-3"
            >
              {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </Button>
          )
        }
      >
        <div className="flex-1">
          {isEditMode && isViewingInfo ? (
            <>
              <div className="bg-white rounded-lg mb-6">
                <div className="bg-gray-50 px-6 py-2 font-medium rounded-t-lg heading-field">
                  Details
                </div>

                <div className="space-y-3 text-sm px-6 py-4">
                  <div className="border-b border-gray-100 pb-2">
                    <p className="mb-1 heading-field">OEM Name</p>
                    <p className="font-medium break-words">
                      {(initialData as any)?.name || 'N/A'}
                    </p>
                  </div>

                  <div className="border-b border-gray-100 pb-2">
                    <p className="mb-1 heading-field">Partnership Status</p>
                    <p className="font-medium break-words">
                      {(initialData as any)?.partnershipStatus === 'ACTIVE'
                        ? 'Active'
                        : 'Not Active'}
                    </p>
                  </div>

                  <div className="border-b border-gray-100 pb-2">
                    <p className="mb-1 heading-field">Dashboard Link</p>
                    <p className="font-medium break-words">
                      {initialData?.dashboardLink || '-'}
                    </p>
                  </div>

                  <div className="border-b border-gray-100 pb-2">
                    <p className="mb-1 heading-field">Dashboard Username</p>
                    <p className="font-medium break-words">
                      {initialData?.dashboardUsername || 'N/A'}
                    </p>
                  </div>

                  <div className="border-b border-gray-100 pb-2">
                    <p className="mb-1 heading-field">Dashboard Password</p>
                    <p className="font-medium break-words">
                      {initialData?.dashboardPassword}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg mb-6">
                <div className="bg-gray-50 px-6 py-2 font-medium rounded-t-lg heading-field">
                  Info
                </div>

                <div className="text-sm px-6 py-4">
                  <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3">
                    <div>
                      <p className="mb-1 heading-field">Created By</p>
                      <p className="font-medium break-words">
                        {(initialData as any)?.createdBy || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1 heading-field">Created Date</p>
                      <p className="font-medium break-words">
                        {(initialData as any)?.createdAt
                          ? dayjs((initialData as any).createdAt).format(
                              'MMM DD, YYYY [at] HH.mm'
                            )
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-3">
                    <div>
                      <p className="mb-1 heading-field">Last Updated By</p>
                      <p className="font-medium break-words">
                        {(initialData as any)?.updatedBy || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1 heading-field">Last Updated</p>
                      <p className="font-medium break-words">
                        {(initialData as any)?.updatedAt
                          ? dayjs((initialData as any).updatedAt).format(
                              'MMM DD, YYYY [at] HH.mm'
                            )
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-0">
                <div className="bg-gray-50 px-6 py-2 font-medium rounded-t-lg heading-field">
                  Detail
                </div>
                <div className="space-y-3 text-sm px-6 py-4">
                  <div className="">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OEM Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      placeholder="e.g. Smoot, Electrum, Charged"
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{
                        accentColor: '#5E2AAC',
                        borderColor: errors.name ? '#EF4444' : '#EBEBEB',
                      }}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dashboard Link <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.dashboardLink}
                        onChange={(e) =>
                          handleInputChange('dashboardLink', e.target.value)
                        }
                        placeholder="e.g. www.fleetelectrum.solution.com"
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${errors.dashboardLink
                          ? 'border-red-500'
                          : 'border-gray-300'
                          }`}
                        style={{
                          accentColor: '#5E2AAC',
                          borderColor: errors.dashboardLink ? '#EF4444' : '#EBEBEB'
                        }}
                      />
                      {errors.dashboardLink && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.dashboardLink}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dashboard Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.dashboardUsername}
                        onChange={(e) =>
                          handleInputChange('dashboardUsername', e.target.value)
                        }
                        placeholder="e.g. DASH_BML"
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${errors.dashboardUsername
                          ? 'border-red-500'
                          : 'border-gray-300'
                          }`}
                        style={{
                          accentColor: '#5E2AAC',
                          borderColor: errors.dashboardUsername ? '#EF4444' : '#EBEBEB'
                        }}
                      />
                      {errors.dashboardUsername && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.dashboardUsername}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dashboard Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.dashboardPassword}
                        onChange={(e) =>
                          handleInputChange('dashboardPassword', e.target.value)
                        }
                        placeholder="e.g. Dash123!"
                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${errors.dashboardPassword
                          ? 'border-red-500'
                          : 'border-gray-300'
                          }`}
                        style={{
                          accentColor: '#5E2AAC',
                          borderColor: errors.dashboardPassword ? '#EF4444' : '#EBEBEB'
                        }}
                      />
                      {errors.dashboardPassword && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.dashboardPassword}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OEM Partnership
                      </label>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={handlePartnershipToggle}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${formData.partnershipStatus === 'ACTIVE'
                            ? 'bg-gray-200'
                            : 'bg-gray-200'
                            }`}
                          style={{
                            backgroundColor:
                              formData.partnershipStatus === 'ACTIVE'
                                ? '#5E2AAC'
                                : '#E5E7EB',
                            accentColor: '#5E2AAC',
                          }}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.partnershipStatus === 'ACTIVE'
                              ? 'translate-x-6'
                              : 'translate-x-1'
                              }`}
                          />
                        </button>
                        <span className="text-sm text-gray-700">
                          {formData.partnershipStatus === 'ACTIVE'
                            ? 'Active'
                            : 'Not Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
      </SideBarModal>
    </>
  );
};

export default AddOEMModal;
